import { Component, EventEmitter, Input, Output } from '@angular/core';
import { QuoteDetail, QuoteLine, QuoteOption } from '../../models/quote';
import { AppModule } from '../../app.module';
import { StockCustomItem, StockGroup, StockItem, StockList } from '../../models/stock';
import { StockService } from '../../shared/stock.service';
import { NotificationService } from '../../shared/notification.service';

@Component({
  selector: 'app-quote-details',
  imports: [AppModule],
  templateUrl: './quote-details.html',
  styleUrl: './quote-details.less'
})
export class QuoteDetails {

  @Input() quote: QuoteDetail;
  @Input() isLoadingLines: boolean = false;
  @Input() isReadOnly: boolean = false;
  @Input() canSelectOptions: boolean = false;
  @Output() saveQuote = new EventEmitter<void>();
  public isShowingAddStockModal = false;
  public isShowingStockGroupsModal = false;
  public isLoadingStock = false;
  public isLoadingStockGroups = false;
  public stockGroups: StockGroup[] = [];
  public incidentStock: StockList;
  public selectedQuoteOptionTabIndex = 0;

  private selectedOption: QuoteOption;

  constructor(private stockService: StockService, public notification: NotificationService) { }

  public optionTotal(option: QuoteOption): number {
    return option.quoteLines.reduce((sum, line) => sum + (line.price * line.qty), 0);
  }

  removeOption(option: QuoteOption) {
    const index = this.quote.quoteOptions.indexOf(option);
    if (index > -1) {
      //faster than filter
      this.quote.quoteOptions.splice(index, 1);
    }
  }

  selectOption(option: QuoteOption) {
    if (this.isReadOnly && this.canSelectOptions) {
      this.quote.quoteOptions = this.quote.quoteOptions.filter(opt => opt.optionNumber === option.optionNumber || opt.optionNumber === 1);
      this.saveQuote.emit();
    }
    else {
      this.quote.quoteOptions = [option];
    }
  }

  removeLineItem(line: QuoteLine, option: QuoteOption) {    
    //splice option does not fire change detection in UI
    option.quoteLines = option.quoteLines.filter(item => item !== line);
  }

  quoteOptionHasCost(option: QuoteOption): boolean {
    return option.quoteLines.some(line => line.cost && line.cost > 0);
  }

  calculateLineItemGP(line: QuoteLine): { randValue: number; percentage: number } {
    const randValue = (line.qty * line.price) - (line.qty * line.cost);
    const percentage = (randValue * 100 / (line.qty * line.price)); //calculated as total being 100%

    return { randValue, percentage };
  }

  calculateQuoteOptionGP(option: QuoteOption): { randValue: number; percentage: number } {
    const totalPrice = option.quoteLines.reduce((sum, line) => sum + (line.qty * line.price), 0);
    const totalCost = option.quoteLines.reduce((sum, line) => sum + (line.qty * line.cost), 0);
    const randValue = totalPrice - totalCost;
    const percentage = (randValue * 100 / totalPrice); //calculated as total being 100%
    return { randValue, percentage };
  }
  
  onClickAddNewItem(option: QuoteOption) {
    
    this.selectedOption = option;
    this.isShowingAddStockModal = true;
    this.isLoadingStock = true;
    this.stockService.getStockForIncident(this.quote.incidentId, this.quote.sellerId).subscribe({
      next: (data) => {
        this.incidentStock = data;
        this.isLoadingStock = false;
      },
      error: (err) => {
        this.isLoadingStock = false;
        this.notification.handleError('Error loading stock for breakdown', err);
      }
    })
  }

  onClickTyreToBeAdded(item: StockItem) {
    const line = new QuoteLine();
    line.description = item.description;
    line.price = item.price;
    line.qty = 1;
    line.msfId = item.msfId;
    line.stockCode = item.stockCode;
    line.quoteId = this.quote.quoteId;
    line.quoteOptionId = this.selectedOption.optionNumber;

    //check if option name is default, if so set to tyre description
    if (this.selectedOption.name === 'New Option') {
      this.selectedOption.name = item.tyreBrand;
    }

    this.selectedOption.quoteLines.push(line);
    this.selectedOption.quoteLines = this.selectedOption.quoteLines.slice(); //force change detection
    this.isShowingAddStockModal = false;
  }

  onClickStockCustomToBeAdded(item: StockCustomItem) {
    const line = new QuoteLine();
    line.description = item.description;
    line.price = item.price;
    line.qty = 1;
    line.stockCustomId = item.stockCustomId;
    line.stockCode = item.stockCode;
    line.quoteId = this.quote.quoteId;
    line.quoteOptionId = this.selectedOption.optionNumber;

    this.selectedOption.quoteLines.push(line);
    this.selectedOption.quoteLines = this.selectedOption.quoteLines.slice(); //force change detection
    this.isShowingAddStockModal = false;
  }

  onClickAddOption() {
    //get the max optionNumber
    const maxOptionNumber = this.quote.quoteOptions.reduce((max, option) => option.optionNumber > max ? option.optionNumber : max, 0);
    const newOption = new QuoteOption();
    newOption.optionNumber = maxOptionNumber + 1;
    newOption.name = 'New Option';
    newOption.quoteId = this.quote.quoteId;
    newOption.total = 0;
    newOption.totalIncl = 0;
    newOption.quoteLines = [];
    this.quote.quoteOptions.push(newOption);
    this.selectedQuoteOptionTabIndex = this.quote.quoteOptions.length - 1;
  }

  onClickAddStockGroups(option: QuoteOption) {
    this.isShowingStockGroupsModal = true;
    this.isLoadingStockGroups = true;
    this.selectedOption = option;
    this.stockService.getStockGroups().subscribe({
      next: (data) => {
        this.stockGroups = data;
        this.isLoadingStockGroups = false;
      },
      error: (err) => {
        this.isLoadingStockGroups = false;
        this.notification.handleError('Error loading stock groups', err);
      }
    })
  }

  onClickStockGroupToBeAdded(group: StockGroup) {
    group.stockItems.forEach(item => {
      const line = new QuoteLine();
      line.description = item.description;
      line.price = item.price;
      line.qty = 1;
      line.stockCustomId = item.stockCustomId;
      line.stockCode = item.stockCode;
      line.quoteId = this.quote.quoteId;
      line.quoteOptionId = this.selectedOption.optionNumber;
      this.selectedOption.quoteLines.push(line);
    });
    this.selectedOption.quoteLines = this.selectedOption.quoteLines.slice(); //force change detection
    this.isShowingStockGroupsModal = false;
  }

}
